/**
 * Нативные окна Telegram (showAlert / showConfirm) вместо браузерных alert() / confirm().
 *
 * В WebView Telegram браузерные alert/confirm выглядят как «окно сайта» (на части устройств
 * с адресом домена в заголовке), а showAlert/showConfirm — как системное окно самого Telegram.
 *
 * Откат на window.alert / window.confirm:
 *  - вне Telegram (обычный браузер) или клиент старше Bot API 6.2 (попапов нет);
 *  - текст длиннее лимита попапа Telegram (256 символов) — иначе клиент бросает ошибку;
 *  - любая ошибка вызова (например, попап уже открыт другим кодом).
 *
 * Окна показываются строго по очереди: Telegram не даёт открыть второй попап, пока открыт первый.
 * В отличие от window.alert, showAlert не блокирует выполнение кода — если после сообщения
 * нужно что-то сделать только когда пользователь его закрыл (reload, переход), используйте await.
 */

type TgWebAppDialogs = {
  showAlert?: (message: string, callback?: () => void) => void;
  showConfirm?: (message: string, callback?: (ok: boolean) => void) => void;
  isVersionAtLeast?: (version: string) => boolean;
};

const POPUP_MAX_LEN = 256;
/** Страховка очереди: если клиент почему-то не вызвал callback, не блокируем следующие окна навсегда. */
const QUEUE_RELEASE_MS = 60_000;

function getTg(): TgWebAppDialogs | null {
  try {
    const tg = (window as unknown as { Telegram?: { WebApp?: TgWebAppDialogs } }).Telegram?.WebApp;
    if (!tg) return null;
    if (typeof tg.isVersionAtLeast === 'function' && !tg.isVersionAtLeast('6.2')) return null;
    return tg;
  } catch {
    return null;
  }
}

function toText(message: unknown): string {
  if (message == null) return '';
  return typeof message === 'string' ? message : String(message);
}

function canUsePopup(text: string): boolean {
  return text.length > 0 && text.length <= POPUP_MAX_LEN;
}

let queue: Promise<unknown> = Promise.resolve();

function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const run = queue.then(task, task);
  queue = Promise.race([
    run,
    new Promise((resolve) => window.setTimeout(resolve, QUEUE_RELEASE_MS))
  ]).catch(() => undefined);
  return run;
}

export function showAlert(message?: unknown): Promise<void> {
  const text = toText(message);
  return enqueue(
    () =>
      new Promise<void>((resolve) => {
        const tg = getTg();
        if (tg && typeof tg.showAlert === 'function' && canUsePopup(text)) {
          try {
            tg.showAlert(text, () => resolve());
            return;
          } catch {
            /* откат на window.alert ниже */
          }
        }
        window.alert(text);
        resolve();
      })
  );
}

export function showConfirm(message?: unknown): Promise<boolean> {
  const text = toText(message);
  return enqueue(
    () =>
      new Promise<boolean>((resolve) => {
        const tg = getTg();
        if (tg && typeof tg.showConfirm === 'function' && canUsePopup(text)) {
          try {
            tg.showConfirm(text, (ok) => resolve(!!ok));
            return;
          } catch {
            /* откат на window.confirm ниже */
          }
        }
        resolve(window.confirm(text));
      })
  );
}
